import React, { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue } from 'react';
import { FaSearch, FaChevronDown, FaCheck, FaTimes } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import axios from 'axios';


const SaleTypeRateDropdown = ({
    transTypeId = '',
    originationSupplier = '7',
    value = '',
    onChange,
    onSelect,
    placeholder = 'Search Sale Type Rate...',
    required = false,
    disabled = false,
    className = '',
    error = '',
    showLabel = true,
    autoFetch = true,
}) => {
    const [rateOptions, setRateOptions] = useState([]);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [localError, setLocalError] = useState('');
    const [selectedRate, setSelectedRate] = useState(value);
    const [isFocused, setIsFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const deferredSearchTerm = useDeferredValue(searchTerm);

    // Professional Styles - Fixed border issues
    const styles = {
        wrapper: {
            position: 'relative',
            width: '100%',
        },
        label: {
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '0.35rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
        },
        labelRequired: {
            color: '#ef4444',
            marginLeft: '0.25rem',
        },
        inputWrapper: {
            position: 'relative',
            width: '100%',
        },
        input: {
            width: '100%',
            padding: '0.75rem 3.5rem 0.75rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '400',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderRadius: '0.75rem',
            backgroundColor: disabled ? '#f1f5f9' : '#ffffff',
            color: '#1e293b',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        },
        inputLoading: {
            backgroundColor: '#f8fafc',
            borderColor: '#cbd5e1',
        },
        inputWithValue: {
            borderColor: '#10b981',
            backgroundColor: '#f0fdf4',
        },
        iconWrapper: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            pointerEvents: 'none',
        },
        searchIcon: {
            color: '#94a3b8',
            fontSize: '0.875rem',
        },
        chevronIcon: {
            color: '#94a3b8',
            fontSize: '0.75rem',
            transition: 'transform 0.25s ease',
            pointerEvents: 'none',
        },
        chevronRotated: {
            transform: 'rotate(180deg)',
        },
        clearButton: {
            position: 'absolute',
            right: '3.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.875rem',
            padding: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease',
            pointerEvents: 'auto',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            zIndex: 2,
        },
        clearButtonHover: {
            backgroundColor: '#f1f5f9',
            color: '#ef4444',
        },
        dropdown: {
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            left: 0,
            right: 0,
            maxHeight: '340px',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
            border: '2px solid #e2e8f0',
            borderRadius: '0.75rem',
            boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.2), 0 4px 20px -8px rgba(0, 0, 0, 0.06)',
            zIndex: 1000,
            display: isOpen ? 'block' : 'none',
            animation: 'slideDown 0.2s ease-out',
        },
        dropdownHeader: {
            padding: '0.5rem 1rem',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8fafc',
            borderRadius: '0.75rem 0.75rem 0 0',
            position: 'sticky',
            top: 0,
            zIndex: 1,
        },
        dropdownHeaderText: {
            fontSize: '0.65rem',
            fontWeight: '600',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        dropdownHeaderCount: {
            fontSize: '0.6rem',
            fontWeight: '500',
            color: '#94a3b8',
            backgroundColor: '#f1f5f9',
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
        },
        dropdownItem: {
            padding: '0.6rem 1rem',
            cursor: 'pointer',
            borderBottom: '1px solid #f8fafc',
            transition: 'all 0.15s ease',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
        },
        dropdownItemSelected: {
            backgroundColor: '#d1fae5',
            borderLeft: '3px solid #10b981',
        },
        dropdownItemHighlighted: {
            backgroundColor: '#f1f5f9',
        },
        dropdownItemRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
        },
        rateIdText: {
            fontWeight: '600',
            color: '#1e293b',
            fontSize: '0.8rem',
        },
        rateDescText: {
            color: '#475569',
            fontSize: '0.85rem',
            lineHeight: '1.4',
            wordBreak: 'break-word',
            marginTop: '0.125rem',
            paddingLeft: '0',
        },
        rateValueBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#065f46',
            fontSize: '0.75rem',
            fontWeight: '600',
            backgroundColor: '#d1fae5',
            padding: '0.2rem 0.6rem',
            borderRadius: '9999px',
            minWidth: '40px',
            flexShrink: 0,
            border: '1px solid #a7f3d0',
        },
        checkmark: {
            color: '#10b981',
            fontSize: '0.875rem',
            marginLeft: '0.5rem',
            flexShrink: 0,
        },
        errorText: {
            marginTop: '0.375rem',
            fontSize: '0.75rem',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
        },
        noResults: {
            padding: '2rem 1rem',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '0.875rem',
        },
        noResultsIcon: {
            fontSize: '2rem',
            marginBottom: '0.5rem',
            display: 'block',
        },
        noResultsText: {
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#64748b',
        },
        noResultsSubText: {
            fontSize: '0.75rem',
            color: '#94a3b8',
            marginTop: '0.25rem',
        },
        loadingContainer: {
            padding: '2rem 1rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
        },
        loadingSpinner: {
            width: '32px',
            height: '32px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
        },
        loadingText: {
            fontSize: '0.875rem',
            color: '#94a3b8',
            fontWeight: '500',
        },
        loadingSubText: {
            fontSize: '0.7rem',
            color: '#cbd5e1',
        },
        selectedBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.65rem',
            fontWeight: '600',
            color: '#10b981',
            backgroundColor: '#d1fae5',
            padding: '0.15rem 0.5rem',
            borderRadius: '9999px',
            marginLeft: '0.5rem',
        },
        selectedDisplay: {
            marginTop: '0.375rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: '#f0fdf4',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            color: '#065f46',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            border: '1px solid #a7f3d0',
        },
        selectedDisplayRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.5rem',
        },
        selectedDisplayText: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            flex: 1,
        },
        selectedDisplayDesc: {
            fontSize: '0.8rem',
            color: '#065f46',
            fontWeight: '500',
        },
        selectedDisplayValue: {
            fontWeight: '600',
            color: '#10b981',
            backgroundColor: '#d1fae5',
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
            fontSize: '0.7rem',
        },
    };

    // Inject keyframe animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            @keyframes slideDown {
                from { 
                    opacity: 0;
                    transform: translateY(-8px) scale(0.98);
                }
                to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                }
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Search function
    const performSearch = useCallback((searchTerm, options) => {
        if (!searchTerm || searchTerm.trim() === '') {
            return options;
        }
        setIsSearching(true);
        const term = searchTerm.toLowerCase().trim();

        const results = options.filter(item => {
            const id = item.ratE_ID?.toString().toLowerCase() || '';
            const desc = item.ratE_DESC?.toLowerCase() || '';
            const value = item.ratE_VALUE?.toString().toLowerCase() || '';
            if (id === term || id.startsWith(term)) return true;
            if (desc === term || desc.startsWith(term)) return true;
            if (value === term || value.startsWith(term)) return true;
            return id.includes(term) || desc.includes(term) || value.includes(term);
        });
        setIsSearching(false);
        return results;
    }, []);

    const debouncedSearch = useMemo(
        () => debounce((value, options, callback) => {
            const results = performSearch(value, options);
            callback(results);
        }, 150),
        [performSearch]
    );

    // Filter options
    useEffect(() => {
        if (deferredSearchTerm.trim() === '') {
            setFilteredOptions(rateOptions);
            setIsSearching(false);
            return;
        }
        debouncedSearch(deferredSearchTerm, rateOptions, (results) => {
            setFilteredOptions(results);
        });
        return () => {
            debouncedSearch.cancel();
        };
    }, [deferredSearchTerm, rateOptions, debouncedSearch]);

    // Reset highlighted index when search results change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [filteredOptions]);

    // Fetch Rate data
    useEffect(() => {
        const fetchRateData = async () => {
            if (!transTypeId) {
                setRateOptions([]);
                setFilteredOptions([]);
                return;
            }

            setIsLoading(true);
            setLocalError('');

            try {
                const response = await axios.get('/api/fbr/sale-type-rate-today', {
                    params: {
                        trans_type_id: transTypeId,
                        origination_supplier: originationSupplier || '7'
                    }
                });

                if (response.data.success) {
                    const data = response.data.data || [];
                    setRateOptions(data);
                    setFilteredOptions(data);
                    
                    // Set initial selection if value is provided
                    if (value) {
                        const matched = data.find(item => item.ratE_ID === parseInt(value));
                        if (matched) {
                            setSearchTerm(`${matched.ratE_ID} - ${matched.ratE_DESC}`);
                            setSelectedRate(value);
                        }
                    }
                } else {
                    setLocalError(response.data.message || 'Failed to fetch rate data');
                    setRateOptions([]);
                    setFilteredOptions([]);
                }
            } catch (err) {
                console.error('Error fetching rate data:', err);
                setLocalError('Failed to load rate data');
                setRateOptions([]);
                setFilteredOptions([]);
            } finally {
                setIsLoading(false);
            }
        };

        if (autoFetch) {
            fetchRateData();
        }
    }, [transTypeId, originationSupplier, value, autoFetch]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setIsFocused(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update selected rate when value prop changes
    useEffect(() => {
        if (value && value !== selectedRate) {
            setSelectedRate(value);
            const selected = rateOptions.find(item => item.ratE_ID === parseInt(value));
            if (selected) {
                setSearchTerm(`${selected.ratE_ID} - ${selected.ratE_DESC}`);
            }
        }
    }, [value, rateOptions, selectedRate]);

    // Handle selection - FIXED: Added null check
    const handleSelect = useCallback((rateId) => {
        if (!rateId) {
            // If rateId is null or undefined, clear selection
            setSelectedRate('');
            setSearchTerm('');
            setIsOpen(false);
            setIsFocused(false);
            setHighlightedIndex(-1);
            // Notify parent with null
            if (onChange) {
                onChange('');
            }
            if (onSelect) {
                onSelect(null);
            }
            return;
        }

        const selected = rateOptions.find(item => item.ratE_ID === rateId);
        if (selected) {
            setSelectedRate(rateId);
            const displayText = `${selected.ratE_ID} - ${selected.ratE_DESC}`;
            setSearchTerm(displayText);

            // Return the full object with id, description, and value
            const result = {
                id: selected.ratE_ID,
                description: selected.ratE_DESC,
                value: selected.ratE_VALUE
            };

            if (onSelect) {
                onSelect(result);
            }
            if (onChange) {
                onChange(rateId);
            }
        }
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
    }, [rateOptions, onSelect, onChange]);

    // Handle keyboard navigation - FIXED: Added null check
    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setIsOpen(true);
                if (filteredOptions.length > 0) {
                    setHighlightedIndex(e.key === 'ArrowDown' ? 0 : filteredOptions.length - 1);
                }
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    const selected = filteredOptions[highlightedIndex];
                    if (selected && selected.ratE_ID) {
                        handleSelect(selected.ratE_ID);
                    }
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    }, [isOpen, filteredOptions, highlightedIndex, handleSelect]);

    // Handle input change - FIXED: Proper null handling
    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setIsOpen(true);
        setLocalError('');
        setHighlightedIndex(-1);

        if (value.trim() === '') {
            // Clear selection when input is cleared
            setSelectedRate('');
            if (onChange) {
                onChange('');
            }
            if (onSelect) {
                onSelect(null);
            }
        }
    }, [onChange, onSelect]);

    // Handle input focus - Clear the input to allow fresh selection
    const handleFocus = useCallback(() => {
        setIsFocused(true);
        // Clear the search term when clicked to allow fresh selection
        setSearchTerm('');
        setIsOpen(true);
        setHighlightedIndex(-1);
        // Don't clear selectedRate, only clear the search term
    }, []);

    // Handle clear button click - Properly clear everything
    const handleClear = useCallback((e) => {
        e.stopPropagation();
        setSearchTerm('');
        setSelectedRate('');
        setIsOpen(false);
        setHighlightedIndex(-1);
        setLocalError('');
        
        // Notify parent about clear
        if (onChange) {
            onChange('');
        }
        if (onSelect) {
            onSelect(null);
        }
        
        // Focus the input after clearing
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    }, [onChange, onSelect]);

    // Get input border color based on state
    const getInputStyles = () => {
        let borderColor = '#e2e8f0';
        let backgroundColor = '#ffffff';
        let boxShadow = '0 1px 3px rgba(0, 0, 0, 0.04)';

        if (error || localError) {
            borderColor = '#ef4444';
        } else if (isFocused) {
            borderColor = '#6366f1';
            boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.12)';
        } else if (selectedRate && !isOpen) {
            borderColor = '#10b981';
            backgroundColor = '#f0fdf4';
            boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.1)';
        } else if (isOpen) {
            borderColor = '#6366f1';
            boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.12)';
        }

        if (isLoading) {
            backgroundColor = '#f8fafc';
            borderColor = '#cbd5e1';
        }

        if (disabled) {
            backgroundColor = '#f1f5f9';
        }

        return { borderColor, backgroundColor, boxShadow };
    };

    const inputStyles = getInputStyles();

    // Get selected rate display - FIXED: Added null check
    const getSelectedDisplay = () => {
        if (selectedRate) {
            const selected = rateOptions.find(item => item.ratE_ID === parseInt(selectedRate));
            if (selected) {
                return {
                    id: selected.ratE_ID,
                    description: selected.ratE_DESC,
                    value: selected.ratE_VALUE
                };
            }
        }
        return null;
    };

    const selectedDisplay = getSelectedDisplay();

    return (
        <div className={className} style={styles.wrapper} ref={dropdownRef}>
            {showLabel && (
                <label style={styles.label}>
                    Tax Rate
                    {required && <span style={styles.labelRequired}>*</span>}
                    {selectedRate && !isLoading && !error && !localError && (
                        <span style={styles.selectedBadge}>
                            <FaCheck size={8} /> Selected
                        </span>
                    )}
                </label>
            )}

            <div style={styles.inputWrapper}>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={isLoading ? 'Loading rates...' : placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isLoading || !transTypeId}
                    style={{
                        ...styles.input,
                        borderColor: inputStyles.borderColor,
                        backgroundColor: inputStyles.backgroundColor,
                        boxShadow: inputStyles.boxShadow,
                    }}
                    onMouseEnter={(e) => {
                        if (!disabled && !isLoading && !error && !localError && !isFocused) {
                            e.currentTarget.style.borderColor = '#818cf8';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isFocused && !isOpen && !error && !localError) {
                            e.currentTarget.style.borderColor = selectedRate ? '#10b981' : '#e2e8f0';
                        }
                    }}
                />

                {/* Clear Button */}
                {selectedRate && !isLoading && !isOpen && (
                    <button
                        style={styles.clearButton}
                        onClick={handleClear}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                            e.currentTarget.style.color = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#94a3b8';
                        }}
                        title="Clear selection"
                        type="button"
                    >
                        <FaTimes size={14} />
                    </button>
                )}

                <div style={styles.iconWrapper}>
                    {isLoading || isSearching ? (
                        <div style={{ 
                            width: '18px', 
                            height: '18px',
                            border: '2px solid #e2e8f0',
                            borderTop: '2px solid #6366f1',
                            borderRadius: '50%',
                            animation: 'spin 0.7s linear infinite',
                        }} />
                    ) : (
                        <FaSearch style={styles.searchIcon} />
                    )}
                    <div style={{
                        ...styles.chevronIcon,
                        ...(isOpen ? styles.chevronRotated : {}),
                    }}>
                        <FaChevronDown />
                    </div>
                </div>

                {/* Dropdown */}
                <div style={styles.dropdown}>
                    {!isLoading && filteredOptions.length > 0 && (
                        <div style={styles.dropdownHeader}>
                            <span style={styles.dropdownHeaderText}>
                                {filteredOptions.length} Rates
                            </span>
                            <span style={styles.dropdownHeaderCount}>
                                {filteredOptions.length} found
                            </span>
                        </div>
                    )}

                    {isLoading && (
                        <div style={styles.loadingContainer}>
                            <div style={styles.loadingSpinner} />
                            <div style={styles.loadingText}>Loading rates...</div>
                            <div style={styles.loadingSubText}>Please wait</div>
                        </div>
                    )}

                    {!isLoading && filteredOptions.length > 0 ? (
                        filteredOptions.slice(0, 100).map((item, index) => (
                            <div
                                key={item.ratE_ID}
                                style={{
                                    ...styles.dropdownItem,
                                    ...(item.ratE_ID === parseInt(selectedRate) ? styles.dropdownItemSelected : {}),
                                    ...(highlightedIndex === index ? styles.dropdownItemHighlighted : {}),
                                }}
                                onMouseEnter={(e) => {
                                    setHighlightedIndex(index);
                                    if (item.ratE_ID !== parseInt(selectedRate)) {
                                        e.currentTarget.style.backgroundColor = '#f8fafc';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (item.ratE_ID !== parseInt(selectedRate)) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                                onClick={() => handleSelect(item.ratE_ID)}
                            >
                                <div style={styles.dropdownItemRow}>
                                    <span style={styles.rateIdText}>Rate ID: {item.ratE_ID}</span>
                                    <span style={styles.rateValueBadge}>{item.ratE_VALUE}%</span>
                                </div>
                                <div style={styles.rateDescText}>{item.ratE_DESC}</div>
                            </div>
                        ))
                    ) : (
                        !isLoading && (
                            <div style={styles.noResults}>
                                <span style={styles.noResultsIcon}>🔍</span>
                                <div style={styles.noResultsText}>
                                    {searchTerm ? 'No matching rates found' : 'No rates available'}
                                </div>
                                {!transTypeId && (
                                    <div style={styles.noResultsSubText}>
                                        Please select a Transaction Type first
                                    </div>
                                )}
                            </div>
                        )
                    )}
                    {filteredOptions.length > 100 && !isLoading && (
                        <div style={{ 
                            padding: '0.5rem 1rem', 
                            textAlign: 'center', 
                            color: '#94a3b8', 
                            fontSize: '0.7rem',
                            borderTop: '1px solid #f1f5f9',
                            backgroundColor: '#f8fafc',
                            borderRadius: '0 0 0.75rem 0.75rem',
                        }}>
                            Showing first 100 results ({filteredOptions.length} total)
                        </div>
                    )}
                </div>
            </div>

            {/* Selected Display */}
            {selectedDisplay && !isLoading && !error && !localError && (
                <div style={styles.selectedDisplay}>
                    <div style={styles.selectedDisplayRow}>
                        <div style={styles.selectedDisplayText}>
                            <span>✓</span>
                            <span style={{ fontWeight: '600' }}>ID: {selectedDisplay.id}</span>
                            <span style={styles.selectedDisplayDesc}>{selectedDisplay.description}</span>
                        </div>
                        <span style={styles.selectedDisplayValue}>
                            {selectedDisplay.value}%
                        </span>
                    </div>
                </div>
            )}

            {(error || localError) && (
                <div style={styles.errorText}>
                    <span>⚠️</span> {error || localError}
                </div>
            )}
        </div>
    );
};

export default SaleTypeRateDropdown;